import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import adminApi from '@/lib/adminApi';
import { formatApiError } from '@/lib/AuthContext';
import { User, Shield, Ban, CheckCircle, Key, Smartphone, Trash2, ArrowUpDown, Clock, Globe, Monitor } from 'lucide-react';

const TABS = ['profile', 'sessions', 'audit'];

export default function AdminUserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('profile');
  const [actionLoading, setActionLoading] = useState('');

  const fetchUser = useCallback(async () => {
    try {
      const { data } = await adminApi.getUser(id);
      setUser(data);
    } catch (error) {
      console.error('Failed to load user:', error);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const handleStatusChange = async (status) => {
    setActionLoading(status);
    try {
      await adminApi.updateUser(id, { status });
      await fetchUser();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
    setActionLoading('');
  };

  const handleRoleChange = async (role) => {
    setActionLoading('role');
    try {
      await adminApi.updateUser(id, { role });
      await fetchUser();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
    setActionLoading('');
  };

  const handleForcePasswordReset = async () => {
    const reason = prompt('Reason for force password reset:');
    if (!reason) return;
    setActionLoading('password');
    try {
      await adminApi.forcePasswordReset(id, reason);
      alert('Password reset forced. All sessions invalidated.');
      await fetchUser();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
    setActionLoading('');
  };

  const handleResetMfa = async () => {
    const reason = prompt('Reason for MFA reset (required):');
    if (!reason) return;
    setActionLoading('mfa');
    try {
      await adminApi.resetMfa(id, reason);
      alert('MFA reset. User will need to re-enrol on next login.');
      await fetchUser();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
    setActionLoading('');
  };

  if (loading) return <AdminLayout><div className="text-[#4E6974]">Loading...</div></AdminLayout>;
  if (!user) return <AdminLayout><p className="text-red-500">User not found</p></AdminLayout>;

  const statusBadge = (s) => {
    const c = { active: 'bg-green-100 text-green-700', disabled: 'bg-yellow-100 text-yellow-700', deleted: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c[s] || 'bg-gray-100 text-gray-600'}`}>{s}</span>;
  };

  return (
    <AdminLayout breadcrumbs={[{ label: 'Users', href: '/admin/users' }, { label: user.name }]}>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2533] flex items-center gap-3" data-testid="admin-user-name">
            <User className="h-6 w-6 text-[#0097A7]" />
            {user.name}
            {user.is_platform_admin && <Shield className="h-5 w-5 text-[#0097A7]" />}
            {statusBadge(user.status)}
          </h1>
          <p className="text-sm text-[#4E6974] mt-1">{user.email} &middot; {user.role} &middot; {user.organisation?.name || 'No org'}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user.status === 'active' && (
            <button data-testid="admin-user-disable-btn" onClick={() => handleStatusChange('disabled')} disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-yellow-300 text-yellow-700 text-sm rounded-lg hover:bg-yellow-50">
              <Ban className="h-3.5 w-3.5" /> Disable
            </button>
          )}
          {user.status === 'disabled' && (
            <button data-testid="admin-user-enable-btn" onClick={() => handleStatusChange('active')} disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-green-300 text-green-600 text-sm rounded-lg hover:bg-green-50">
              <CheckCircle className="h-3.5 w-3.5" /> Enable
            </button>
          )}
          {user.status !== 'deleted' && (
            <button data-testid="admin-user-delete-btn" onClick={() => { if (window.confirm('Soft-delete this user? They will be hard-deleted after 30 days.')) handleStatusChange('deleted'); }} disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-600 text-sm rounded-lg hover:bg-red-50">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
          <button data-testid="admin-user-force-password-btn" onClick={handleForcePasswordReset} disabled={!!actionLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E0E0E0] text-[#4E6974] text-sm rounded-lg hover:bg-[#F5F5F5]">
            <Key className="h-3.5 w-3.5" /> Force password reset
          </button>
          {user.mfa_enabled && (
            <button data-testid="admin-user-reset-mfa-btn" onClick={handleResetMfa} disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E0E0E0] text-[#4E6974] text-sm rounded-lg hover:bg-[#F5F5F5]">
              <Smartphone className="h-3.5 w-3.5" /> Reset MFA
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-[#E8ECF0] mb-6">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm capitalize transition-colors ${tab === t ? 'text-[#0097A7] border-b-2 border-[#0097A7] font-medium' : 'text-[#4E6974] hover:text-[#0A2533]'}`}
            data-testid={`admin-user-tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === 'profile' && (
        <div className="grid grid-cols-2 gap-4" data-testid="admin-user-profile">
          {[
            ['Email', user.email], ['Name', user.name], ['Role', user.role],
            ['Organisation', user.organisation?.name || 'N/A'], ['Persona', user.persona || 'N/A'],
            ['MFA', user.mfa_enabled ? 'Enabled' : 'Disabled'], ['Status', user.status],
            ['Email verified', user.email_verified ? 'Yes' : 'No'],
            ['Last login', user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'],
            ['Created', new Date(user.created_at).toLocaleString()],
          ].map(([l, v]) => (
            <div key={l} className="bg-white rounded-lg border border-[#E8ECF0] p-4">
              <p className="text-xs text-[#4E6974] mb-1">{l}</p>
              <p className="text-sm font-medium text-[#0A2533] capitalize">{v}</p>
            </div>
          ))}
          <div className="col-span-2 bg-white rounded-lg border border-[#E8ECF0] p-4">
            <label className="text-xs text-[#4E6974] block mb-1">Change role</label>
            <select value={user.role} onChange={e => handleRoleChange(e.target.value)} data-testid="admin-user-role-select"
              className="border border-[#E0E0E0] rounded-lg px-3 py-1.5 text-sm">
              <option value="member">Member</option><option value="admin">Admin</option><option value="owner">Owner</option>
            </select>
          </div>
        </div>
      )}

      {tab === 'sessions' && (
        <div className="bg-white rounded-xl border border-[#E8ECF0] overflow-hidden" data-testid="admin-user-sessions">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8ECF0] bg-[#F8FAFB]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">IP Address</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">User Agent</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Active</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Created</th>
              </tr>
            </thead>
            <tbody>
              {(user.sessions || []).length === 0 && <tr><td colSpan={4} className="text-center py-6 text-[#B0BEC5]">No sessions</td></tr>}
              {(user.sessions || []).map((s, i) => (
                <tr key={i} className="border-b border-[#F5F5F5]">
                  <td className="px-4 py-3 text-[#0A2533] font-mono text-xs">{s.ip_address}</td>
                  <td className="px-4 py-3 text-[#4E6974] text-xs max-w-[300px] truncate">{s.user_agent}</td>
                  <td className="px-4 py-3">{s.active ? <span className="text-green-600 text-xs">Active</span> : <span className="text-[#B0BEC5] text-xs">Expired</span>}</td>
                  <td className="px-4 py-3 text-[#B0BEC5] text-xs">{new Date(s.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'audit' && (
        <div className="bg-white rounded-xl border border-[#E8ECF0] p-5 space-y-2" data-testid="admin-user-audit">
          {(user.audit_log || []).length === 0 && <p className="text-sm text-[#B0BEC5]">No audit entries</p>}
          {(user.audit_log || []).map(entry => (
            <div key={entry.id} className="flex items-center gap-3 py-2 border-b border-[#F5F5F5] last:border-0">
              <Clock className="h-3.5 w-3.5 text-[#B0BEC5]" />
              <span className="text-sm text-[#0A2533] flex-1">{entry.action}</span>
              <span className="text-xs text-[#B0BEC5]">{entry.ip_address}</span>
              <span className="text-xs text-[#B0BEC5]">{new Date(entry.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
